/// <reference types="tizen-common-web" />

enum LogLevel {
  /**
   * Critical conditions
   * @type {number}
   */
  Critical = 4,

  /**
   * Error conditions
   * @type {number}
   */
  Error = 3,

  /**
   * Warning conditions
   * @type {number}
   */
  Warn = 2,

  /**
   * Informational messages
   * @type {number}
   */
  Info = 1,

  /**
   * Debug-level messages
   * @type {number}
   */
  Debug = 0,
}

/**
 * A module that provides error logging and console message interception.
 * Can draw logs on top of the application in a full-screen overlay.
 * Used for debugging applications, especially on TVs where Chili or Weinre won't run.
 *
 * Should be loaded before the entire application.
 * Should not contain external dependencies.
 * Should not use anything that requires polyfill coverage!
 *
 * To call the overlay, you need to type the following spell on the TV remote: ↑↑↓↓←→←→
 */

type RemoteButton = 'up' | 'down' | 'left' | 'right' | '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';

class LoadGuard {
  private readonly logs: {msg: string; lvl: LogLevel}[] = [];
  private readonly MAX_LOGS: number = 1000;
  private overlay: HTMLElement | null = null;
  private isVisible: boolean = false;

  private readonly mappingKeys = this.getKeyMappings();

  private readonly showSequences: RemoteButton[][] = [
    // Enter code: ↑↑↓↓←→←→
    ['up', 'up', 'down', 'down', 'left', 'right', 'left', 'right'],
    // Service code: 1173
    ['1', '1', '7', '3'],
  ];

  /**
   * History of last pressed keys on remote/keyboard
   */
  private readonly currentSequence: RemoteButton[] = [];

  constructor() {
    this.addErrorHandler();
    this.addUnhandledRejectionHandler();
    this.interceptConsole();
    this.addControlKeysHandlers();
    this.monitorScriptsAndStyles();
    this.addLog('Console guard initialized', LogLevel.Debug);

    // this.show(); // todo disable
  }

  /**
   * Get key mappings for various remotes
   * @private
   */
  private getKeyMappings(): Record<RemoteButton, Array<number | undefined>> {
    const tizenKey = (action: string) => window.tizen?.tvinputdevice?.getKey?.(action)?.code;
    const androidTvKey = (action: `VK_${string}`) =>
      (window[action as keyof typeof window] || undefined) as number | undefined;

    return {
      // keyboard, android TV, Phillips, Samsung/Tizen, Samsung/Orsay, other vendors
      'up': [38, 19, androidTvKey('VK_UP'), tizenKey('ArrowUp'), 29460],
      'down': [40, 20, androidTvKey('VK_DOWN'), tizenKey('ArrowDown'), 29461],
      'left': [37, 21, androidTvKey('VK_LEFT'), tizenKey('ArrowLeft'), 4],
      'right': [39, 22, androidTvKey('VK_RIGHT'), tizenKey('ArrowRight'), 5],
      '0': [48, 96, androidTvKey('VK_0'), tizenKey('0'), 17],
      '1': [49, 97, androidTvKey('VK_1'), tizenKey('1'), 101],
      '2': [50, 98, androidTvKey('VK_2'), tizenKey('2'), 98],
      '3': [51, 99, androidTvKey('VK_3'), tizenKey('3'), 6],
      '4': [52, 100, androidTvKey('VK_4'), tizenKey('4'), 8],
      '5': [53, 101, androidTvKey('VK_5'), tizenKey('5'), 9],
      '6': [54, 102, androidTvKey('VK_6'), tizenKey('6'), 10],
      '7': [55, 103, androidTvKey('VK_7'), tizenKey('7'), 12],
      '8': [56, 104, androidTvKey('VK_8'), tizenKey('8'), 13],
      '9': [57, 105, androidTvKey('VK_9'), tizenKey('9'), 14],
    };
  }

  private addErrorHandler() {
    // Set up error handling (compatible with old browsers)

    const originalOnErrorHandler = window.onerror;

    window.onerror = (
      message: string | Event,
      filename: string | undefined,
      lineno: number | undefined,
      colno: number | undefined,
      error: Error | undefined,
    ) => {
      // handle other event handlers
      if (originalOnErrorHandler && typeof originalOnErrorHandler === 'function' && originalOnErrorHandler.call) {
        try {
          originalOnErrorHandler.call(window, message, filename, lineno, colno, error);
        } catch (e) {
          const msg = e
            ? this.ensureString(e) || this.ensureString((e as Error).message) || this.ensureString((e as Error).name)
            : 'Unknown error';
          this.addLog(`External "window.onerror" handler error: ${msg}`, LogLevel.Critical);
        }
      }

      const errorEvent = message as ErrorEvent | undefined;
      filename = errorEvent?.filename || filename;
      lineno = errorEvent?.lineno || lineno;
      colno = errorEvent?.colno || colno;
      error = (errorEvent?.error || error) as Error | undefined;

      const target = errorEvent?.target;

      const msg =
        this.ensureString(errorEvent?.message) ||
        this.ensureString(message) ||
        this.ensureString(error?.message) ||
        this.ensureString(error?.name) ||
        '';
      const stack = this.ensureString((error as Error)?.stack);

      if (
        window.HTMLLinkElement &&
        typeof window.HTMLLinkElement === 'function' &&
        target instanceof window.HTMLLinkElement
      ) {
        this.addLog(`Unable to load style: "${target.href}" ${msg}`, LogLevel.Warn);
      } else if (
        window.HTMLScriptElement &&
        typeof window.HTMLScriptElement === 'function' &&
        target instanceof window.HTMLScriptElement
      ) {
        this.addLog(`Unable to load script file: "${target.src}" ${msg}`, LogLevel.Error);
      } else if (
        window.HTMLVideoElement &&
        typeof window.HTMLVideoElement === 'function' &&
        target instanceof window.HTMLVideoElement
      ) {
        this.addLog(`Unable to load video file: "${target.src || target.currentSrc}" ${msg}`, LogLevel.Warn);
      } else if (
        window.HTMLImageElement &&
        typeof window.HTMLImageElement === 'function' &&
        target instanceof window.HTMLImageElement
      ) {
        this.addLog(`Unable to load image: "${target.src || target.srcset}" ${msg}`, LogLevel.Warn);
      } else if (msg) {
        if (stack) {
          this.addLog(stack, LogLevel.Error);
        }
        this.addLog(
          `Unhandled runtime error: "${msg}"` +
            (filename ? ` filename: ${filename}` : '') +
            (lineno != null ? ` lineno: ${lineno}` : '') +
            (colno != null ? ` colno: ${colno}` : ''),
          LogLevel.Error,
        );
      } else {
        if (stack) {
          this.addLog(stack, LogLevel.Error);
        }
        this.addLog('Unhandled unknown runtime error!', LogLevel.Error);
      }

      return false;
    };
  }

  private addUnhandledRejectionHandler() {
    // Set up promise rejection handling (if supported)
    if (!window.addEventListener) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    window.addEventListener(
      'unhandledrejection',
      function (event) {
        const reason =
          (event
            ? that.ensureString(event) ||
              that.ensureString((event as unknown as Error)?.message) ||
              that.ensureString(((event as unknown as ErrorEvent)?.error as Error)?.message) ||
              that.ensureString(event?.reason) ||
              that.ensureString((event?.reason as Error)?.message) ||
              that.ensureString(event?.detail)
            : undefined) || 'Unknown rejection';
        that.addLog('Unhandled Promise Rejection: ' + reason, LogLevel.Error);
      },
      false,
    );
  }

  private interceptConsole() {
    try {
      if (!window.console) {
        return;
      }

      const methods = ['log', 'debug', 'info', 'warn', 'error'] as Array<
        ('log' | 'debug' | 'info' | 'warn' | 'error') & keyof Console
      >;
      for (const methodName of methods) {
        const method = window.console[methodName];
        if (method != null && typeof method === 'function') {
          const originalMethod = method;

          window.console[methodName] = (...args: any[]) => {
            // Call the original method
            originalMethod.apply(window.console, args);

            // Log to our system
            try {
              let argsStr = '';
              for (let i = 0; i < args.length; i++) {
                const arg = args[i] as unknown;
                argsStr = argsStr + ' ' + this.safeStringify(arg);
              }

              let level = LogLevel.Debug;
              switch (methodName) {
                case 'log':
                  level = LogLevel.Info;
                  break;
                case 'debug':
                  level = LogLevel.Debug;
                  break;
                case 'info':
                  level = LogLevel.Info;
                  break;
                case 'warn':
                  level = LogLevel.Warn;
                  break;
                case 'error':
                  level = LogLevel.Error;
                  break;
              }

              this.addLog(`[console.${methodName}]${argsStr}`, level);

              // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
              // Silent fail to prevent recursion
            }
          };
        }
      }
    } catch (e) {
      console.error('Unable to monkey-patch console methods', e);
    }
  }

  private ensureString(s: any) {
    return typeof s === 'string' ? s : undefined;
  }

  private safeStringify(obj: any): string {
    try {
      if (obj === null) return 'null';
      if (obj === undefined) return 'undefined';
      if (typeof obj === 'string') return obj;
      if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj);

      // Try JSON.stringify first
      if (typeof JSON !== 'undefined' && JSON.stringify) {
        try {
          return JSON.stringify(obj, null, 2);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (jsonError) {
          // Fallback for circular references or other issues
        }
      }

      // Fallback for old browsers or circular references
      if (typeof obj === 'object') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (obj.toString && typeof obj.toString === 'function') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
          const result = obj.toString() as string;
          if (result !== '[object Object]') return result;
        }

        // Simple object serialization
        const parts: string[] = [];
        for (const key in obj) {
          const objField = obj[key];
          const objFieldType = typeof objField;
          if (
            obj.hasOwnProperty &&
            obj.hasOwnProperty(key) &&
            (!objField || objFieldType === 'string' || objFieldType === 'number' || objFieldType === 'boolean')
          ) {
            parts.push(key + ': ' + this.safeStringify(objField));
          }
        }
        return '{' + parts.join(', ') + '}';
      }

      return String(obj);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return '[Object: Unable to serialize]';
    }
  }

  private addControlKeysHandlers() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const originalKeydown = document.onkeydown;
    document.onkeydown = function (event) {
      if (originalKeydown && typeof originalKeydown === 'function') {
        originalKeydown.call(this, event);
      }

      const keyCode = event?.keyCode || event?.which;
      if (keyCode == null) {
        return;
      }

      that.handleShowSequence(keyCode);
      that.handleScrollKeys(keyCode);
    };
  }

  private handleShowSequence(keyCode: number) {
    let pressedButton: RemoteButton | undefined;
    for (const btn in this.mappingKeys) {
      const codes = this.mappingKeys[btn as RemoteButton];
      if (codes.indexOf(keyCode) !== -1) {
        pressedButton = btn as RemoteButton;
        break;
      }
    }

    if (!pressedButton) {
      return;
    }

    this.currentSequence.push(pressedButton);

    const maxSequenceLength = Math.max(...this.showSequences.map((s) => s.length));
    if (this.currentSequence.length > maxSequenceLength) {
      this.currentSequence.shift();
    }

    for (const sequence of this.showSequences) {
      if (this.currentSequence.length < sequence.length) {
        continue;
      }

      const lastN = this.currentSequence.slice(-sequence.length);
      let match = true;
      for (let i = 0; i < sequence.length; i++) {
        if (sequence[i] !== lastN[i]) {
          match = false;
          break;
        }
      }

      if (match) {
        this.toggleOverlay();
        this.currentSequence.length = 0;
        return;
      }
    }
  }

  private handleScrollKeys(keyCode: number) {
    if (!this.isVisible || !this.overlay) {
      return;
    }
    const content = this.overlay.firstChild as HTMLElement;
    const scrollAmount = 100;

    // Up arrow
    if (this.mappingKeys.up.indexOf(keyCode) !== -1) {
      content.scrollTop -= scrollAmount;
      if (content.scrollTop <= 0) {
        content.scrollTop = 0;
      }
      return;
    }

    // Down arrow
    if (this.mappingKeys.down.indexOf(keyCode) !== -1) {
      content.scrollTop += scrollAmount;
      return;
    }

    // Left arrow
    if (this.mappingKeys.left.indexOf(keyCode) !== -1) {
      content.scrollTop = 0;
      return;
    }

    // Right arrow
    if (this.mappingKeys.right.indexOf(keyCode) !== -1) {
      content.scrollTop = content.scrollHeight;
      return;
    }
  }

  private monitorScriptsAndStyles() {
    try {
      document.addEventListener(
        'load',
        (event) => {
          if (!event || !event.target) {
            return;
          }
          const el = event.target as HTMLScriptElement | HTMLLinkElement;

          if (el.tagName === 'SCRIPT' || el.tagName === 'LINK') {
            const src = (el as HTMLScriptElement).src || (el as HTMLLinkElement).href;
            this.addLog(`${el.tagName} loaded successfully: ${src}`, LogLevel.Debug);
          }
        },
        true,
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      this.addLog(`Unable to initialize script loading monitoring`, LogLevel.Critical);
    }
  }

  private createOverlay(): HTMLElement | null {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    overlay.style.color = '#00ff00';
    overlay.style.fontFamily = 'Consolas, Monaco, "Courier New", monospace';
    overlay.style.fontSize = '18px';
    overlay.style.padding = '20px';
    overlay.style.boxSizing = 'border-box';
    overlay.style.zIndex = '999999';
    overlay.style.overflow = 'hidden';

    const content = document.createElement('div');
    content.style.width = '100%';
    content.style.height = '100%';
    content.style.overflow = 'auto';
    content.style.whiteSpace = 'pre-wrap';
    content.style.wordBreak = 'break-word';

    overlay.appendChild(content);

    return overlay;
  }

  private updateOverlay(logEntry?: (typeof this.logs)[0]) {
    if (!this.overlay) {
      return;
    }

    const content = this.overlay.firstChild as HTMLElement;
    if (!content) {
      return;
    }
    if (content.innerHTML === '') {
      for (let i = 0; i < this.logs.length; i++) {
        const entry = this.logs[i];
        const el = this.createLogEntryElement(entry);
        content.appendChild(el);
      }
    } else if (logEntry) {
      const el = this.createLogEntryElement(logEntry);
      content.insertBefore(el, content.firstChild);
    }
  }

  private createLogEntryElement(entry: (typeof this.logs)[0]) {
    const element = document.createElement('div');
    element.innerText = entry.msg;
    if (entry.lvl === LogLevel.Debug) {
      element.style.color = 'silver';
    } else if (entry.lvl === LogLevel.Info) {
      element.style.color = 'white';
    } else if (entry.lvl === LogLevel.Warn) {
      element.style.color = '#e87a20';
    } else if (entry.lvl === LogLevel.Error) {
      element.style.color = 'red';
    } else if (entry.lvl === LogLevel.Critical) {
      element.style.color = 'darkred';
    }
    return element;
  }

  /**
   * Toggle console overlay
   */
  public toggleOverlay() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Show console overlay
   */
  public show() {
    if (this.isVisible) {
      return;
    }

    this.overlay = this.createOverlay();
    if (!this.overlay) {
      return;
    }

    document.body.appendChild(this.overlay);
    this.updateOverlay();
    this.isVisible = true;
  }

  /**
   * Hide console overlay
   */
  public hide() {
    if (!this.isVisible || !this.overlay) {
      this.overlay = null;
      this.isVisible = false;
      return;
    }

    if (this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }

    this.overlay = null;
    this.isVisible = false;
  }

  public addLog(message: string, level = LogLevel.Debug) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      msg: `${timestamp} ${message}`,
      lvl: level,
    };

    this.logs.unshift(logEntry);

    // Limit log size
    if (this.logs.length > this.MAX_LOGS) {
      this.logs.pop();
    }

    // Update overlay if visible
    if (this.isVisible && this.overlay) {
      this.updateOverlay(logEntry);
    }
  }
}

// Initialize the console guard
(() => {
  try {
    window.hoteza = window.hoteza || {};
    window.hoteza.loadGuard = new LoadGuard();
  } catch (error) {
    console.error('[loadGuard] Unable to initialize', error);
  }
})();
